
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { useToast } from "@/hooks/use-toast";

interface VideoProgressProps {
  resourceId?: string;
  fileId?: string;
}

export function useVideoProgress({ resourceId, fileId }: VideoProgressProps) {
  const { session } = useSession();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const updateVideoProgress = useCallback(async (progress: number, duration: number) => {
    if (!session?.user?.id || !resourceId || !fileId) {
      console.log('Unable to update progress - missing user, resource or file ID');
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // Calculate percentage completed
      const percentCompleted = Math.round((progress / duration) * 100);
      console.log(`Video progress: ${percentCompleted}% (${progress}/${duration})`);
      
      // Only mark as complete if they've watched at least 90% of the video
      const isComplete = percentCompleted >= 90;
      
      const { error } = await supabase
        .from('training_progress')
        .upsert({
          resource_id: resourceId,
          file_id: fileId,
          user_id: session.user.id,
          completed: isComplete,
          completed_at: isComplete ? new Date().toISOString() : null,
          progress_percentage: percentCompleted,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,file_id',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error updating video progress:', error);
        toast({
          title: "Error saving progress",
          description: "Your video progress may not be saved properly",
          variant: "destructive",
        });
      } else if (isComplete) {
        console.log('Video marked as complete');
      }
    } catch (error) {
      console.error('Error in updateVideoProgress:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [session?.user?.id, resourceId, fileId, toast]);

  return {
    isProcessing,
    updateVideoProgress
  };
}
